import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useBillingStatus } from "../hooks/useBillingStatus.js";
import { getDigestSettings, updateDigestSettings, sendTestDigest } from "../api.js";
import type { DigestSettings } from "@tuit/shared-types";
import styles from "../styles/pages/SettingsPage.module.css";

export function DigestPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { status: billing } = useBillingStatus();
  const [settings, setSettings] = useState<DigestSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isLoaded && !isSignedIn) navigate({ to: "/" });
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!isSignedIn) return;
    getDigestSettings().then(setSettings).catch(() => {});
  }, [isSignedIn]);

  if (!isLoaded || !isSignedIn) return null;

  const canDigest = billing?.capabilities.canEmailDigest ?? false;

  async function handleSave(patch: Partial<DigestSettings>) {
    setSaving(true);
    try {
      const updated = await updateDigestSettings(patch);
      setSettings(updated);
      flash("Saved");
    } catch {
      flash("Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    try {
      await sendTestDigest();
      flash("Test email sent (check console in dev)");
    } catch (err) {
      flash(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setTesting(false);
    }
  }

  function flash(text: string) {
    setMsg(text);
    setTimeout(() => setMsg(null), 3500);
  }

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        <Link to="/profile" className={styles.back}>← Profile</Link>
      </nav>
      <div className={styles.inner}>
        <h1 className={styles.heading}>Email Digest</h1>
        <p className={styles.sub}>Get a curated summary of your feeds delivered by email.</p>

        {!canDigest ? (
          <div className={styles.upsell}>
            <p>Email digests are available on <strong>Plus</strong> and <strong>Pro</strong> plans.</p>
            <Link to="/premium" className={styles.upsellLink}>Upgrade now →</Link>
          </div>
        ) : settings ? (
          <div className={styles.card}>
            <div className={styles.field}>
              <label className={styles.label}>Delivery email</label>
              <input
                type="email"
                className={styles.input}
                defaultValue={settings.deliveryEmail ?? ""}
                onBlur={(e) => handleSave({ deliveryEmail: e.target.value || null })}
                placeholder="you@example.com"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Schedule</label>
              <select
                className={styles.input}
                value={settings.schedule}
                onChange={(e) => handleSave({ schedule: e.target.value as DigestSettings["schedule"] })}
              >
                <option value="twice_daily">Twice daily</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => handleSave({ enabled: e.target.checked })}
                />
                Enable digest emails
              </label>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.secondaryBtn} onClick={handleTest} disabled={testing || saving}>
                {testing ? "Sending…" : "Send test email"}
              </button>
              {saving && <span className={styles.hint}>Saving…</span>}
            </div>
          </div>
        ) : (
          <p className={styles.hint}>Loading…</p>
        )}

        {msg && <p className={styles.flash}>{msg}</p>}
      </div>
    </div>
  );
}
